import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  CaretUpIcon,
  CaretDownIcon,
  CaretRightIcon,
  SidebarSimpleIcon,
  WarningIcon,
  MagnifyingGlassIcon,
  ArrowLeftIcon,
} from "@phosphor-icons/react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { bucketUrlPrefix } from "common/config";
import {
  AccessibilityCategory,
  AccessibilityTag,
  ReviewPhoto,
  VenueDetails,
} from "common/types";
import { Share } from "lucide-react";
import { useDarkMode } from "@/hooks/useDarkMode";
import FullImageViewer from "@/components/FullImageViewer";
import AnalyticsService from "@/services/analytics";
import { createPortal } from "react-dom";
import { PiFlagPennant } from "react-icons/pi";
import FlagVenueAccessibility from "@/features/flag-report/FlagVenueAccessibility";
import { useAuth, useZoomDetection, useI18N } from "common/hooks";
import { useAtom } from "jotai";
import {
  actionToPerformAfterLoginAtom,
  actionToPerformValueAfterLoginAtom,
} from "@/store/atoms";
import { useAuthModals, AuthModalScreen } from "@/hooks/useAuthModals";
import { Tooltip } from "react-tooltip";

// ============================================================================
// Types
// ============================================================================

// Unified photo type that works for both Basic and Hybrid
interface UnifiedPhoto {
  path: string;
  path_thumb?: string;
  alt_text?: string;
  tag_name?: string;
  tag_applied?: string;
  created_at?: string;
  width?: number;
  height?: number;
  user_roles?: {
    isGeneralUser?: boolean;
  };
}

// Unified tag type that works for both Basic and Hybrid
interface UnifiedTag {
  tagId?: number;
  tagName: string;
  categoryDisplaySectionId?: number;
  categoryDisplaySectionName?: string;
  // Properties needed for flag functionality
  tag_image?: string;
  tag_image_dark?: string;
  categoryId?: number;
  questionId?: number;
}

interface SpaceItem {
  id: string;
  name: string;
  categoryId: number;
  categoryName: string;
  image: string;
  image_dark: string;
  photos: UnifiedPhoto[];
  tags: UnifiedTag[];
  qtagsAll: string;
  isEmpty?: boolean;
  // Additional fields for hybrid data
  level?: string;
  levelId?: number;
  // Category path for flag modal (format:"categoryTitle > subCategoryTitle > levelName" for hybrid)
  categoryPath?: string;
}

interface AccessibilityDetailViewProps {
  venueDetails: VenueDetails;
  accessibilityCategories?: AccessibilityCategory[];
  initialSpaceId?: string;
  onClose?: () => void;
  onShare?: (currentSpaceId: string) => void;
  pageName?: string; // For analytics tracking - defaults to "Search - Accessibility Detail"
}

// ============================================================================
// Sub-components
// ============================================================================

// --- Space Navigation Header ---
interface SpaceNavigationHeaderProps {
  venueName: string;
  currentSpace: SpaceItem | null;
  onPrevious: () => void;
  onNext: () => void;
  onToggleSidebar: () => void;
  onBack?: () => void;
  isSidebarOpen: boolean;
  hasPrevious: boolean;
  hasNext: boolean;
}

function SpaceNavigationHeader({
  venueName,
  currentSpace,
  onPrevious,
  onNext,
  onToggleSidebar,
  onBack,
  isSidebarOpen,
  hasPrevious,
  hasNext,
}: SpaceNavigationHeaderProps) {
  const i18n = useI18N();
  
  return (
    <header className="flex flex-col bg-bg-brand-secondary-default relative z-10">
      {/* Row 1: Breadcrumb Navigation */}
      <div className="flex items-center gap-2 px-5 pt-3">
        <nav aria-label="Breadcrumb" className="flex items-center gap-2">
          {/* Home / Venue Name */}
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-2 text-fg-neutral-strong hover:text-fg-brand-subtle transition-colors"
            aria-label="Back to venue details"
          >
            <ArrowLeftIcon size={20} className="flex-shrink-0 text-fg-neutral-strong" />
            <span className="label-m-medium truncate max-w-[300px] lg:max-w-[400px]">
              {venueName}
            </span>
          </button>

        </nav>
      </div>

      {/* Row 2: Space Navigation Controls */}
      <div className="flex items-center gap-3 px-4 md:pl-5 pb-1 border-b border-border-token-brand-minimal">
        {/* Sidebar Toggle + Divider + Space Navigation grouped together */}
        <div className="flex items-center gap-2">
          <button
            id="accessibility-sidebar-toggle-btn"
            type="button"
            onClick={onToggleSidebar}
            className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-bg-brand-secondary-hover transition-colors"
            aria-label={i18n.get("toggleSidebar")}
            aria-expanded={isSidebarOpen}
          >
            <SidebarSimpleIcon size={20} className="text-fg-neutral-strong" />
          </button>
          <Tooltip
            anchorSelect="#accessibility-sidebar-toggle-btn"
            content={i18n.get("toggleSidebar")}
            place="bottom"
            style={{ zIndex: 9999 }}
          />

          {/* Divider between sidebar toggle and nav arrows */}
          <div
            className="w-px h-6 border border-border-token-neutral-minimal"
            aria-hidden="true"
          />
          <button
            id="accessibility-prev-space-btn"
            type="button"
            onClick={onPrevious}
            disabled={!hasPrevious}
            className="flex items-center justify-center w-8 h-8 rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed
            bg-bg-brand-ghost-default
            hover:bg-bg-brand-ghost-hover 
            active:bg-bg-brand-ghost-active
            focus:bg-bg-brand-ghost-focus
            disabled:bg-bg-brand-ghost-disabled
            "
            aria-label={i18n.get("previousSpace")}
          >
            <CaretUpIcon size={20} className="text-fg-neutral-strong" />
          </button>
          <Tooltip
            anchorSelect="#accessibility-prev-space-btn"
            content={i18n.get("previousSpace")}
            place="bottom"
            style={{ zIndex: 9999 }}
          />
          <button
            id="accessibility-next-space-btn"
            type="button"
            onClick={onNext}
            disabled={!hasNext}
            className="flex items-center justify-center w-8 h-8 rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed
            bg-bg-brand-ghost-default
            hover:bg-bg-brand-ghost-hover 
            active:bg-bg-brand-ghost-active
            focus:bg-bg-brand-ghost-focus
            disabled:bg-bg-brand-ghost-disabled
            "
            aria-label={i18n.get("nextSpace")}
          >
            <CaretDownIcon size={20} className="text-fg-neutral-strong" />
          </button>
          <Tooltip
            anchorSelect="#accessibility-next-space-btn"
            content={i18n.get("nextSpace")}
            place="bottom"
            style={{ zIndex: 9999 }}
          />
        </div>

        {/* Current Space Name */}
        <h1 className="flex flex-1 title-m-bold text-fg-neutral-strong truncate">
          {currentSpace?.name || i18n.get("selectASpace")}
        </h1>
      </div>
    </header>
  );
}

// --- Space Sidebar (Tree Navigation) ---
interface SpaceSidebarProps {
  spaces: SpaceItem[];
  categories: { id: number; name: string; image: string; image_dark: string }[];
  selectedSpaceId: string | null;
  onSelectSpace: (spaceId: string) => void;
  expandedCategories: Set<number>;
  onToggleCategory: (categoryId: number) => void;
  onToggleSidebar: () => void;
  isSidebarOpen?: boolean;
}

// Tree node type for keyboard navigation
interface TreeNode {
  id: string;
  type: 'category' | 'space';
  categoryId?: number;
  spaceId?: string;
  hasChildren?: boolean;
  isExpanded?: boolean;
}

function SpaceSidebar({
  spaces,
  categories,
  selectedSpaceId,
  onSelectSpace,
  expandedCategories,
  onToggleCategory,
  onToggleSidebar,
  isSidebarOpen = true,
}: SpaceSidebarProps) {
  const isDark = useDarkMode();
  const i18n = useI18N();
  const [searchQuery, setSearchQuery] = useState("");
  
  // Track which treeitem has focus/is focusable (roving tabindex pattern)
  const [focusedNodeId, setFocusedNodeId] = useState<string | null>(
    () => selectedSpaceId || (spaces.find((s) => !s.isEmpty)?.id ?? null)
  );

  // Count non-empty spaces
  const nonEmptySpacesCount = useMemo(() => {
    return spaces.filter((space) => !space.isEmpty).length;
  }, [spaces]);

  // Filter categories and spaces based on search query
  const { filteredCategories, filteredSpacesByCategory } = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    
    if (!query) {
      // No search query - return all
      const grouped: Record<number, SpaceItem[]> = {};
      spaces.forEach((space) => {
        if (!grouped[space.categoryId]) {
          grouped[space.categoryId] = [];
        }
        grouped[space.categoryId].push(space);
      });
      return {
        filteredCategories: categories,
        filteredSpacesByCategory: grouped,
      };
    }

    // Filter spaces that match the search query
    const matchingSpaces = spaces.filter((space) => {
      return (
        space.name.toLowerCase().includes(query) ||
        space.categoryName.toLowerCase().includes(query)
      );
    });

    // Group matching spaces by category
    const grouped: Record<number, SpaceItem[]> = {};
    matchingSpaces.forEach((space) => {
      if (!grouped[space.categoryId]) {
        grouped[space.categoryId] = [];
      }
      grouped[space.categoryId].push(space);
    });

    // Filter categories that have matching spaces or match the query themselves
    const matchingCategories = categories.filter((category) => {
      return (
        category.name.toLowerCase().includes(query) ||
        grouped[category.id]?.length > 0
      );
    });

    return {
      filteredCategories: matchingCategories,
      filteredSpacesByCategory: grouped,
    };
  }, [spaces, categories, searchQuery]);

  const filteredSpacesCount = useMemo(() => {
    if (!searchQuery.trim()) return nonEmptySpacesCount;
    return Object.values(filteredSpacesByCategory).reduce(
      (sum, spaceList) => sum + spaceList.filter((s) => !s.isEmpty).length,
      0
    );
  }, [searchQuery, filteredSpacesByCategory, nonEmptySpacesCount]);

  // Build flat tree for keyboard navigation
  const buildFlatTree = (): TreeNode[] => {
    const flat: TreeNode[] = [];
    filteredCategories.forEach((category) => {
      const categorySpaces = filteredSpacesByCategory[category.id] || [];
      const isExpanded = expandedCategories.has(category.id);
      flat.push({
        id: `cat-${category.id}`,
        type: 'category',
        categoryId: category.id,
        hasChildren: categorySpaces.length > 0,
        isExpanded,
      });
      if (isExpanded) {
        categorySpaces.forEach((space) => {
          if (!space.isEmpty) {
            flat.push({
              id: space.id,
              type: 'space',
              spaceId: space.id,
            });
          }
        });
      }
    });
    return flat;
  };

  // Keyboard navigation handler for tree
  const handleTreeKeyDown = (e: React.KeyboardEvent) => {
    const flatTree = buildFlatTree();
    const currentIndex = flatTree.findIndex((node) => node.id === focusedNodeId);
    if (currentIndex === -1) return;

    const currentNode = flatTree[currentIndex];

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (currentIndex < flatTree.length - 1) {
          setFocusedNodeId(flatTree[currentIndex + 1].id);
        }
        break;

      case 'ArrowUp':
        e.preventDefault();
        if (currentIndex > 0) {
          setFocusedNodeId(flatTree[currentIndex - 1].id);
        }
        break;

      case 'ArrowRightIcon':
        e.preventDefault();
        if (currentNode.type === 'category' && currentNode.hasChildren) {
          if (!currentNode.isExpanded && currentNode.categoryId !== undefined) {
            onToggleCategory(currentNode.categoryId);
          } else if (currentIndex < flatTree.length - 1) {
            // Move to first child
            setFocusedNodeId(flatTree[currentIndex + 1].id);
          }
        }
        break;

      case 'ArrowLeftIcon':
        e.preventDefault();
        if (currentNode.type === 'category' && currentNode.isExpanded && currentNode.categoryId !== undefined) {
          onToggleCategory(currentNode.categoryId);
        } else if (currentNode.type === 'space') {
          // Move to parent category
          const parentCat = flatTree
            .slice(0, currentIndex)
            .reverse()
            .find((n) => n.type === 'category');
          if (parentCat) setFocusedNodeId(parentCat.id);
        }
        break;

      case 'Home':
        e.preventDefault();
        if (flatTree.length > 0) {
          setFocusedNodeId(flatTree[0].id);
        }
        break;

      case 'End':
        e.preventDefault();
        if (flatTree.length > 0) {
          setFocusedNodeId(flatTree[flatTree.length - 1].id);
        }
        break;

      case 'Enter':
      case ' ':
        e.preventDefault();
        if (currentNode.type === 'category') {
          if (currentNode.hasChildren && currentNode.categoryId !== undefined) {
            onToggleCategory(currentNode.categoryId);
          }
        } else if (currentNode.type === 'space' && currentNode.spaceId) {
          onSelectSpace(currentNode.spaceId);
        }
        break;
    }
  };

  // Sync focus to DOM when focusedNodeId changes
  useEffect(() => {
    if (focusedNodeId) {
      const element = document.querySelector(`[data-node-id="${focusedNodeId}"]`);
      if (element instanceof HTMLElement) {
        element.focus();
      }
    }
  }, [focusedNodeId]);

  // Update focusedNodeId when selection changes
  useEffect(() => {
    if (selectedSpaceId) {
      setFocusedNodeId(selectedSpaceId);
    }
  }, [selectedSpaceId]);

  return (
    <nav
      className="flex flex-col h-full bg-bg-brand-secondary-default border-r border-border-token-brand-minimal"
      aria-label="Space navigation"
    >
      {/* Header with count and close button */}
      <div className="flex items-center justify-between pt-4 pl-5 pr-4 ">
        <h2 className="label-l-bold text-fg-neutral-strong">
          {filteredSpacesCount} Spaces
        </h2>
        <button
          id="accessibility-sidebar-close-btn"
          type="button"
          onClick={onToggleSidebar}
          className="lg:hidden flex items-center justify-center w-8 h-8 rounded-full hover:bg-bg-brand-secondary-hover transition-colors"
          aria-label={i18n.get("closeSidebar")}
          aria-expanded={isSidebarOpen}
        >
          <SidebarSimpleIcon size={20} className="text-fg-neutral-strong" />
        </button>
        <Tooltip
          anchorSelect="#accessibility-sidebar-close-btn"
          content={i18n.get("closeSidebar")}
          place="bottom"
          style={{ zIndex: 9999 }}
        />
      </div>

      {/* Search Input */}
      <div className="pt-4 pl-4 pr-4">
        <div className="relative">
          <MagnifyingGlassIcon
            size={20}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-neutral-subtle"
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={i18n.get("searchLabel")}
            className="w-full h-12 pl-10 pr-4 py-2 bg-bg-brand-secondary-default border border-border-token-brand-minimal rounded-lg text-base md:text-[13px] text-fg-neutral-strong placeholder:text-fg-neutral-subtle focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {filteredCategories.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <p className="label-m-medium text-fg-neutral-subtle">
              {i18n.get("noSpacesFoundMatching")} "{searchQuery}"
            </p>
          </div>
        ) : (
          <ul 
            role="tree" 
            aria-label="Spaces navigation"
            className="flex flex-col gap-6"
            onKeyDown={handleTreeKeyDown}
          >
            {filteredCategories.map((category) => {
              const categorySpaces = filteredSpacesByCategory[category.id] || [];
              const isExpanded = expandedCategories.has(category.id);
              const hasSpaces = categorySpaces.length > 0;
              const categoryNodeId = `cat-${category.id}`;

            return (
              <li key={category.id}>
                {/* Category Header */}
                <button
                  type="button"
                  role="treeitem"
                  aria-level={1}
                  aria-expanded={hasSpaces ? isExpanded : undefined}
                  aria-selected={false}
                  data-node-id={categoryNodeId}
                  tabIndex={focusedNodeId === categoryNodeId ? 0 : -1}
                  onClick={() => {
                    if (hasSpaces) {
                      onToggleCategory(category.id);
                    }
                  }}
                  className="flex items-center gap-4 w-full py-2 text-left"
                >
                  {category.image && (
                    <img
                      src={
                        bucketUrlPrefix +
                        (isDark && category.image_dark
                          ? category.image_dark
                          : category.image)
                      }
                      alt={category.name}
                      className={`w-6 h-6 flex-shrink-0 ${isDark && !category.image_dark ? "filter brightness-0 invert" : ""}`}
                    />
                  )}

                  <span className="flex-1 label-m-semibold text-fg-neutral-strong">
                    {category.name}
                  </span>

                  {hasSpaces && (
                    isExpanded
                      ? <CaretUpIcon size={16} className="text-fg-neutral-strong" aria-hidden="true" />
                      : <CaretDownIcon size={16} className="text-fg-neutral-strong" aria-hidden="true" />
                  )}
                </button>

                {/* Subcategories (Spaces) */}
                {isExpanded && hasSpaces && (
                  <ul 
                    role="group"
                    className="mt-1 ml-3 border-l-2 border-border-token-brand-minimal"
                  >
                    {categorySpaces.map((space) => {
                      const isSelected = selectedSpaceId === space.id;

                      return (
                        <li key={space.id}>
                          <button
                            type="button"
                            role="treeitem"
                            aria-level={2}
                            aria-selected={isSelected}
                            data-node-id={space.id}
                            tabIndex={focusedNodeId === space.id ? 0 : -1}
                            onClick={() => onSelectSpace(space.id)}
                            className={`flex items-center gap-3 w-full pl-4 pr-3 py-2 text-left rounded-r-lg transition-colors ${
                              isSelected
                                ? "bg-container-brand-minimal border-l-2 border-primary-500 -ml-0.5"
                                : "hover:bg-bg-brand-secondary-hover"
                            } ${space.isEmpty ? "opacity-50" : ""}`}
                          >
                            <span
                              className={`flex-1 label-m-medium ${
                                isSelected
                                  ? "text-fg-brand-subtle"
                                  : space.isEmpty
                                    ? "text-fg-neutral-minimal"
                                    : "text-fg-neutral-strong"
                              }`}
                            >
                              {space.isEmpty
                                ? i18n.get("noSpaceFound").replace("{category}", space.categoryName.toLowerCase())
                                : space.name}
                            </span>                            
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}

                {/* Empty Category Message */}
                {isExpanded && !hasSpaces && (
                  <div className="ml-10 mt-1 label-m-medium text-fg-neutral-minimal">
                    {i18n.get("noSpaceFound").replace("{category}", category.name.toLowerCase())}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
        )}
      </div>
    </nav>
  );
}

// --- Image Carousel ---
interface AccessibilityImageCarouselProps {
  photos: UnifiedPhoto[];
  currentIndex: number;
  onIndexChange: (index: number) => void;
  onImageClick?: () => void;
  isZoomedIn?: boolean;
  pageName?: string;
}

function AccessibilityImageCarousel({
  photos,
  currentIndex,
  onIndexChange,
  onImageClick,
  isZoomedIn = false,
  pageName = "Search - Accessibility Detail",
}: AccessibilityImageCarouselProps) {
  const i18n = useI18N();
  const photoScrollContainerRef = useRef<HTMLDivElement>(null);
  const currentPhoto = photos[currentIndex];
  const hasPhotos = photos.length > 0;

  // Sync scroll position whenever currentIndex changes (e.g. parent resets to 0 on space switch)
  useEffect(() => {
    if (photoScrollContainerRef.current) {
      const container = photoScrollContainerRef.current;
      container.scrollTo({ left: currentIndex * container.clientWidth, behavior: "auto" });
    }
  }, [currentIndex]);

  // Determine event names based on pageName
  const getEventName = (baseName: string) => {
    if (pageName === "Accessibility Space Guide") {
      const eventMap: Record<string, string> = {
        "searchAccessibilityPhotoViewed": "accessibilitySpaceGuidePhotoViewed",
      };
      return eventMap[baseName] || baseName;
    }
    return baseName;
  };

  const handlePrevImage = () => {
    if (photoScrollContainerRef.current && photos.length > 0) {
      const eventName = getEventName("searchAccessibilityPhotoViewed");
      AnalyticsService.trackGAEventHandler(eventName);
      AnalyticsService.trackMx(eventName, {
        Direction: "previous",
        PhotoIndex: currentIndex > 0 ? currentIndex - 1 : photos.length - 1,
      });
      const container = photoScrollContainerRef.current;
      const scrollAmount = container.clientWidth;
      const newIndex = currentIndex > 0 ? currentIndex - 1 : photos.length - 1;
      const isLooping = currentIndex === 0;
      
      // Instant scroll when looping, smooth scroll for normal navigation
      container.scrollTo({ 
        left: newIndex * scrollAmount, 
        behavior: isLooping ? "auto" : "smooth" 
      });
      
      onIndexChange(newIndex);
    }
  };

  const handleNextImage = () => {
    if (photoScrollContainerRef.current && photos.length > 0) {
      const eventName = getEventName("searchAccessibilityPhotoViewed");
      AnalyticsService.trackGAEventHandler(eventName);
      AnalyticsService.trackMx(eventName, {
        Direction: "next",
        PhotoIndex: currentIndex < photos.length - 1 ? currentIndex + 1 : 0,
      });
      const container = photoScrollContainerRef.current;
      const scrollAmount = container.clientWidth;
      const newIndex = currentIndex < photos.length - 1 ? currentIndex + 1 : 0;
      const isLooping = currentIndex === photos.length - 1;
      
      // Instant scroll when looping, smooth scroll for normal navigation
      container.scrollTo({ 
        left: newIndex * scrollAmount, 
        behavior: isLooping ? "auto" : "smooth" 
      });
      
      onIndexChange(newIndex);
    }
  };

  return (
    <div className="relative w-full flex flex-col overflow-hidden h-full">
      {hasPhotos ? (
        <>
          {/* Image Container - Fixed height on mobile, flexible on desktop */}
          <div className="relative overflow-hidden h-[300px] lg:h-full lg:flex-1 bg-container-brand-minimal">
            {/* Scrollable Carousel Container */}
            <div
              ref={photoScrollContainerRef}
              className="flex w-full h-full overflow-x-auto scrollbar-hide snap-x snap-mandatory"
              role="list"
              aria-label="Accessibility photos"
            >
              {photos.map((photo, index) => (
                <div
                  key={`photo-${index}`}
                  className={`flex-shrink-0 w-full h-full snap-center flex items-center justify-center`}
                >
                  <button
                    onClick={onImageClick}
                    className={`focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 rounded-lg cursor-pointer group relative h-full w-full flex items-center justify-center ${isZoomedIn ? '' : 'p-2 lg:p-3'}`}
                    aria-label={`Open image ${index + 1} of ${photos.length} in full screen`}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onImageClick?.();
                      }
                    }}
                  >
                    <img
                      src={bucketUrlPrefix + photo.path}
                      alt={photo.alt_text || i18n.get("accessibilityFeature")}
                      className="object-contain rounded-lg w-auto h-auto max-w-full max-h-full lg:max-h-[60vh]"
                    />
                    {/* Hover gradient overlay */}
                    <div className="absolute inset-0 bg-[#070708] opacity-0 group-hover:opacity-[0.12] group-active:opacity-[0.12] transition-opacity duration-200 pointer-events-none" />
                  </button>
                </div>
              ))}
            </div>

            {/* Mobile Carousel Navigation - Left/Right Overlay Buttons */}
            {photos.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={handlePrevImage}
                  className="flex absolute left-3 top-1/2 -translate-y-1/2 z-10 items-center justify-center w-10 h-10 bg-surface-secondary border border-border-token-brand-minimal rounded-full hover:bg-bg-brand-ghost-hover transition-all focus-blue shadow-md hover:shadow-lg"
                  aria-label="Previous photo"
                >
                  <ChevronRight
                    size={18}
                    className="rotate-180 text-fg-neutral-strong"
                  />
                </button>

                <button
                  type="button"
                  onClick={handleNextImage}
                  className="flex absolute right-3 top-1/2 -translate-y-1/2 z-10 items-center justify-center w-10 h-10 bg-surface-secondary border border-border-token-brand-minimal rounded-full hover:bg-bg-brand-ghost-hover transition-all focus-blue shadow-md hover:shadow-lg"
                  aria-label="Next photo"
                >
                  <ChevronRight size={18} className="text-fg-neutral-strong" />
                </button>
              </>
            )}

            {/* Image Counter - Over the image at bottom */}
            <div className="absolute bottom-3 md:bottom-[30px] left-1/2 -translate-x-1/2 z-10 bg-black/70 text-white label-m-medium px-3 py-1 rounded-full font-medium">
              {currentIndex + 1}/{photos.length}
            </div>
          </div>


          {/* Fixed Control Panel - Desktop only, showing counter at bottom */}
          <div className="hidden lg:flex flex-shrink-0 bg-bg-brand-secondary-default backdrop-blur-sm border-t border-gray-200 shadow-lg flex-col">
            {/* Alt Text Section - Fixed height to prevent layout shift */}
            <div className="px-4 py-2 h-20 flex items-center justify-center">
              {currentPhoto.alt_text && (
                <p className="label-s-medium text-fg-neutral-strong text-center leading-relaxed line-clamp-3 overflow-y-auto">
                  {currentPhoto.alt_text}
                </p>
              )}
            </div>

            {/* Navigation Controls */}
            <div className="flex items-center justify-center px-4 py-3">
              <div className="items-center gap-3  border border-gray-600 rounded-full px-3 py-2 hidden">
                {/* Previous Button */}
                <button
                  onClick={handlePrevImage}
                  disabled={photos.length <= 1}
                  className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  aria-label="Previous image"
                >
                  <ChevronLeft size={16} className="text-fg-neutral-strong" />
                </button>

                {/* Image Counter */}
                <div className="body-m-medium text-fg-neutral-strong px-3 py-1">
                  {currentIndex + 1}/{photos.length}
                </div>

                {/* Next Button */}
                <button
                  onClick={handleNextImage}
                  disabled={photos.length <= 1}
                  className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  aria-label="Next image"
                >
                  <ChevronRight size={16} className="text-fg-neutral-strong" />
                </button>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="flex justify-center items-center h-[300px] lg:h-full bg-container-brand-minimal">
          <div className="flex flex-col items-center justify-center text-fg-neutral-subtle  h-[300px]">
            <div className="w-24 h-24 bg-container-neutral-minimal rounded-lg flex items-center justify-center mb-4">
              {/* Background layer - Checkerboard pattern */}
              <div
                className="absolute inset-0 w-full h-full"
                style={{
                  backgroundImage:
                    "url(/images/No_Data_Placeholder_Images_Background.png)",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                }}
              />
              {/* Foreground layer - Sociability icon centered */}
              <img
                src="/images/No_Data_Placeholder_Images_Foreground.png"
                alt=""
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[200px] h-[200px] object-contain z-10"
                aria-hidden="true"
              />
            </div>
            <p className="title-s-semibold">{i18n.get("noPhotosAvailable")}</p>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Accessibility Tags Panel ---
interface AccessibilityTagsPanelProps {
  spaceName: string;
  spaceId: string;
  tags: UnifiedTag[];
  displaySections?: Record<string, string>;
  alertsCount?: number;
  onFlag?: () => void;
  onShare?: (spaceId: string) => void;
}

function AccessibilityTagsPanel({
  spaceName,
  spaceId,
  tags,
  displaySections,
  alertsCount = 0,
  onFlag,
  onShare,
}: AccessibilityTagsPanelProps) {
  const i18n = useI18N();
  // Group tags by their display section
  const tagsBySection = useMemo(() => {
    const sections: Record<string, string[]> = {};

    tags.forEach((tag) => {
      // Use display section name if available from tag or from displaySections lookup
      const sectionKey = tag.categoryDisplaySectionId?.toString() || "general";
      const sectionName =
        tag.categoryDisplaySectionName ||
        displaySections?.[sectionKey] ||
        (sectionKey === "general" ? i18n.get("general") : `Section ${sectionKey}`);

      if (!sections[sectionName]) {
        sections[sectionName] = [];
      }
      sections[sectionName].push(tag.tagName);
    });

    return sections;
  }, [tags, displaySections]);

  const sectionNames = Object.keys(tagsBySection);

  return (
    <aside
      className="flex flex-col lg:h-full bg-bg-brand-secondary-default lg:border-l border-border-token-brand-minimal"
      aria-label="Accessibility information"
    >
      {/* Header */}
      <header className="flex items-center justify-between p-4 border-t lg:border-t-0 border-b border-border-token-brand-minimal">
        <h2 className="hidden md:block label-l-bold text-fg-neutral-strong">{i18n.get("accessibility")}</h2>
        <h2 className="md:hidden label-l-bold text-fg-neutral-strong">{spaceName}</h2>

        <div className="flex items-center gap-2">
          {onFlag && (
            <>
              <button
                id="accessibility-flag-btn"
                type="button"
                onClick={onFlag}
                className="flex items-center justify-center w-8 h-8 rounded-full bg-bg-brand-secondary-default border border-border-token-brand-minimal hover:bg-bg-brand-secondary-hover transition-colors"
                aria-label={i18n.get("reportAnIssue")}
              >
                <PiFlagPennant
                  size={20}
                  className="text-fg-neutral-strong"
                  aria-hidden="true"
                />
              </button>
              <Tooltip
                anchorSelect="#accessibility-flag-btn"
                content={i18n.get("reportAnIssue")}
                place="bottom"
              />
            </>
          )}
          {onShare && (
            <>
              <button
                id="accessibility-share-btn"
                type="button"
                onClick={() => onShare(spaceId)}
                className="flex items-center justify-center w-8 h-8 rounded-full bg-bg-brand-secondary-default border border-border-token-brand-minimal hover:bg-bg-brand-secondary-hover transition-colors"
                aria-label={i18n.get("shareAccessibilityInformation")}
              >
                <Share size={16} className="text-fg-neutral-strong" />
              </button>
              <Tooltip
                anchorSelect="#accessibility-share-btn"
                content={i18n.get("share")}
                place="bottom"
              />
            </>
          )}
        </div>
      </header>

      {/* Alerts Banner */}
      {alertsCount > 0 && (
        <a
          href="#alerts"
          className="flex items-center gap-2 mx-6 mt-6 p-2 bg-error-50 rounded-lg"
        >
          <WarningIcon size={24} className="text-fg-error-subtle flex-shrink-0" />
          <span className="flex-1 body-l-semibold text-fg-neutral-strong">
            {i18n.get("accessibilityAlerts")}
          </span>
          <span className="label-s-medium text-fg-neutral-strong">
            {alertsCount}
          </span>
          <CaretRightIcon size={24} className="text-fg-neutral-strong" />
        </a>
      )}

      {/* Tags by Section */}
      <div className="lg:flex-1 lg:overflow-y-auto p-4">
        <div className="flex flex-col gap-3">
          {sectionNames.length > 0 ? (
            sectionNames.map((sectionName, sectionIndex) => (
              <React.Fragment key={sectionName}>
                <div className="flex flex-col gap-1">
                  <h3 className="body-m-medium text-fg-brand-subtle">
                    {sectionName}
                  </h3>
                  <ul className="flex flex-col gap-1 list-disc list-inside pl-2">
                    {tagsBySection[sectionName].map((tagName, tagIndex) => (
                      <li
                        key={tagIndex}
                        className="body-m-medium text-fg-neutral-strong"
                      >
                        {tagName}
                      </li>
                    ))}
                  </ul>
                </div>

                {sectionIndex < sectionNames.length - 1 && (
                  <hr
                    className="border-border-token-brand-minimal"
                    aria-hidden="true"
                  />
                )}
              </React.Fragment>
            ))
          ) : (
            <p className="label-m-medium text-fg-neutral-subtle text-center py-8">
              {i18n.get("noAccessibilityTagsAvailable")}
            </p>
          )}
        </div>
      </div>
    </aside>
  );
}

// ============================================================================
// Main Component
// ============================================================================

function AccessibilityDetailView({
  venueDetails,
  accessibilityCategories,
  initialSpaceId,
  onClose,
  onShare,
  pageName = "Search - Accessibility Detail",
}: AccessibilityDetailViewProps) {

  // Determine event name prefix based on pageName
  const isSpaceGuide = pageName === "Accessibility Space Guide";
  const getEventName = (baseName: string) => {
    if (isSpaceGuide) {
      // Map search events to space guide events
      const eventMap: Record<string, string> = {
        "searchAccessibilityPhotoViewed": "accessibilitySpaceGuidePhotoViewed",
        "flagAccessibilityReportClose": "accessibilitySpaceGuideFlagClosed",
        "flagAccessibilityReportOpen": "accessibilitySpaceGuideFlagOpened",
        "searchAccessibilitySpaceSelected": "accessibilitySpaceGuideSpaceSelected",
        "accessibilityFullImageOpen": "accessibilitySpaceGuideFullImageOpened",
        "accessibilityFullImageClose": "accessibilitySpaceGuideFullImageClosed",
      };
      return eventMap[baseName] || baseName;
    }
    return baseName;
  };

  // Flag functionality state and hooks
  const [isFlagVenueOpen, setIsFlagVenueOpen] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [selectedTagForFlag, setSelectedTagForFlag] = useState<any>(null);
  const [selectedCategoryName, setSelectedCategoryName] = useState<string>("");
  const { user, isLoggedIn } = useAuth();
  const [actionToPerformAfterLogin, setActionToPerformAfterLogin] = useAtom(
    actionToPerformAfterLoginAtom,
  );
  const [actionToPerformValueAfterLogin, setActionToPerformValueAfterLogin] =
    useAtom(actionToPerformValueAfterLoginAtom);
  const { showModal } = useAuthModals();
  const isZoomedIn = useZoomDetection();
  const i18n = useI18N();

  // Handle login callback for flag functionality
  useEffect(() => {
    if (isLoggedIn && user && actionToPerformAfterLogin) {
      if (actionToPerformAfterLogin === "flagAccessibility") {
        try {
          const parsedValue = JSON.parse(actionToPerformValueAfterLogin);
          if (parsedValue.tag) {
            setSelectedTagForFlag(parsedValue.tag);
            setSelectedCategoryName(parsedValue.categoryName || "");
          } else {
            // Fallback for older format (just the tag)
            setSelectedTagForFlag(parsedValue);
            setSelectedCategoryName("");
          }
        } catch {
          setSelectedTagForFlag(actionToPerformValueAfterLogin);
          setSelectedCategoryName("");
        }
        setIsFlagVenueOpen(true);
      }
      setActionToPerformAfterLogin("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionToPerformAfterLogin, user, isLoggedIn]);

  const handleFlagVenueAccessibilityClose = () => {
    const eventName = getEventName("flagAccessibilityReportClose");
    AnalyticsService.trackGAEventHandler(eventName);
    AnalyticsService.trackMx(eventName, {});
    setIsFlagVenueOpen(false);
  };

  // Filter out items with user_roles.isGeneralUser === true
  const filteredAccessibilityCategories = useMemo(() => {
    return (accessibilityCategories || [])
      .map((category) => ({
        ...category,
        tags: category.tags
          .map((tag) => ({
            ...tag,
            reviewPhotos:
              tag.reviewPhotos?.filter(
                (photo) => photo.user_roles?.isGeneralUser !== true,
              ) || [],
            qtags:
              tag.qtags?.filter(
                (qtag) => qtag.user_roles?.isGeneralUser !== true,
              ) || [],
          }))
          .filter(
            (tag) =>
              // Keep tags that have either reviewPhotos or qtags remaining after filtering
              (tag.reviewPhotos && tag.reviewPhotos.length > 0) ||
              (tag.qtags && tag.qtags.length > 0),
          ),
      }))
      .filter(
        (category) =>
          // Keep categories that have at least one tag remaining after filtering
          category.tags.length > 0,
      );
  }, [accessibilityCategories]);

  // Build flat list of spaces from categories
  const { spaces, categories, displaySectionsMap } = useMemo(() => {
    // Helper function to convert ReviewPhoto to UnifiedPhoto
    const convertBasicPhoto = (photo: ReviewPhoto): UnifiedPhoto => ({
      path: photo.path,
      path_thumb: photo.path_thumb,
      alt_text: photo.alt_text,
      tag_name: photo.tag_name,
      tag_applied: photo.tag_applied,
      created_at: photo.created_at,
      width: photo.width,
      height: photo.height,
      user_roles: photo.user_roles,
    });

    // Helper function to convert AccessibilityTag to UnifiedTag
    const convertBasicTag = (tag: AccessibilityTag): UnifiedTag => ({
      tagId: tag.tagId,
      tagName: tag.tagName,
      categoryDisplaySectionId: tag.categoryDisplaySectionId,
      categoryDisplaySectionName: tag.categoryDisplaySectionName,
      tag_image: tag.tag_image,
      tag_image_dark: tag.tag_image_dark,
      categoryId: tag.categoryId,
      questionId: tag.questionId,
    });

    const allSpaces: SpaceItem[] = [];
    const allCategories: {
      id: number;
      name: string;
      image: string;
      image_dark: string;
    }[] = [];
    const displaySections: Record<number, Record<string, string>> = {};

    // Process Basic data
    filteredAccessibilityCategories.forEach((category) => {
      allCategories.push({
        id: category.catId,
        name: category.catName,
        image: category.catImage,
        image_dark: category.catImageDark,
      });

      // Store display sections for basic categories
      if (category.cat_display_sections) {
        displaySections[category.catId] = category.cat_display_sections;
      }

      if (category.tags.length === 0) {
        // Add empty placeholder
        allSpaces.push({
          id: `empty-${category.catId}`,
          name: category.catName,
          categoryId: category.catId,
          categoryName: category.catName,
          image: category.catImage,
          image_dark: category.catImageDark,
          photos: [],
          tags: [],
          qtagsAll: "",
          isEmpty: true,
          categoryPath: category.catName,
        });
      } else {
        category.tags.forEach((tag, index) => {
          const mainTagName = tag.qtagsAll.split(",")[0];
          allSpaces.push({
            id: `${category.catId}-${index}`,
            name: mainTagName,
            categoryId: category.catId,
            categoryName: category.catName,
            image: category.catImage,
            image_dark: category.catImageDark,
            photos: (tag.reviewPhotos || []).map(convertBasicPhoto),
            tags: (tag.qtags || []).map(convertBasicTag),
            qtagsAll: tag.qtagsAll,
            isEmpty: false,
            categoryPath: category.catName,
          });
        });
      }
    });

    return {
      spaces: allSpaces,
      categories: allCategories,
      displaySectionsMap: displaySections,
    };
  }, [filteredAccessibilityCategories]);

  // State - Initialize showSidebar based on screen size
  const [showSidebar, setShowSidebar] = useState(() => {
    // Check if window is available (SSR safety)
    if (typeof window !== 'undefined') {
      // Default to true on desktop (lg breakpoint is 1024px), false on mobile
      return window.innerWidth >= 1024;
    }
    return true;
  });
  const [selectedSpaceId, setSelectedSpaceId] = useState<string | null>(
    initialSpaceId ||
      (spaces.length > 0 ? spaces.find((s) => !s.isEmpty)?.id || null : null),
  );
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(
    () => new Set(categories.map((c) => c.id)),
  );
  const [fullImageView, setFullImageView] = useState<{
    show: boolean;
    current: number;
  }>({
    show: false,
    current: 0,
  });

  // Handle responsive sidebar state on window resize
  useEffect(() => {
    const handleResize = () => {
      const isDesktop = window.innerWidth >= 1024;
      // Only auto-adjust on resize if transitioning between mobile and desktop
      if (isDesktop && !showSidebar) {
        // User resized to desktop, open sidebar by default
        setShowSidebar(true);
      } else if (!isDesktop && showSidebar) {
        // User resized to mobile, close sidebar by default
        setShowSidebar(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [showSidebar]);

  // Handle ESC key to close sidebar on mobile
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showSidebar && window.innerWidth < 1024) {
        setShowSidebar(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showSidebar]);

  // Prevent body scroll when mobile drawer is open
  useEffect(() => {
    if (showSidebar && typeof window !== 'undefined' && window.innerWidth < 1024) {
      // Save current scroll position
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';

      return () => {
        // Restore scroll position
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [showSidebar]);

  // Current space
  const currentSpace = useMemo(
    () => spaces.find((s) => s.id === selectedSpaceId) || null,
    [spaces, selectedSpaceId],
  );

  // Navigation
  const nonEmptySpaces = spaces.filter((s) => !s.isEmpty);
  const currentNonEmptyIndex = nonEmptySpaces.findIndex(
    (s) => s.id === selectedSpaceId,
  );

  const handlePreviousSpace = () => {
    if (currentNonEmptyIndex > 0) {
      const newSpace = nonEmptySpaces[currentNonEmptyIndex - 1];
      const eventName = getEventName("searchAccessibilitySpaceSelected");
      AnalyticsService.trackGAEventHandler(eventName);
      AnalyticsService.trackMx(eventName, {
        SpaceId: newSpace.id,
        SpaceName: newSpace.name,
        Direction: "previous",
      });
      setSelectedSpaceId(newSpace.id);
      setCurrentImageIndex(0);
    }
  };

  const handleNextSpace = () => {
    if (currentNonEmptyIndex < nonEmptySpaces.length - 1) {
      const newSpace = nonEmptySpaces[currentNonEmptyIndex + 1];
      const eventName = getEventName("searchAccessibilitySpaceSelected");
      AnalyticsService.trackGAEventHandler(eventName);
      AnalyticsService.trackMx(eventName, {
        SpaceId: newSpace.id,
        SpaceName: newSpace.name,
        Direction: "next",
      });
      setSelectedSpaceId(newSpace.id);
      setCurrentImageIndex(0);
    }
  };

  const handleSelectSpace = (spaceId: string) => {
    const space = spaces.find((s) => s.id === spaceId);
    if (space && !space.isEmpty) {
      const eventName = getEventName("searchAccessibilitySpaceSelected");
      AnalyticsService.trackGAEventHandler(eventName);
      AnalyticsService.trackMx(eventName, {
        SpaceId: space.id,
        SpaceName: space.name,
        Direction: "sidebar",
      });
      setSelectedSpaceId(spaceId);
      setCurrentImageIndex(0);
      // Close sidebar on mobile after selecting a space
      if (typeof window !== 'undefined' && window.innerWidth < 1024) {
        setShowSidebar(false);
      }
    }
  };

  const handleToggleCategory = (categoryId: number) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const handleFlag = () => {
    if (currentSpace && currentSpace.tags.length > 0) {
      const mainTag = currentSpace.tags[0];
      // Use categoryPath for the display name (includes full path for hybrid venues)
      const displayCategoryName =
        currentSpace.categoryPath || currentSpace.categoryName;
      if (!user || !isLoggedIn) {
        console.log("setActionToPerformAfterLogin in the Atom");
        setActionToPerformAfterLogin("flagAccessibility");
        // Store both the tag and category name for after login
        setActionToPerformValueAfterLogin(
          JSON.stringify({
            tag: mainTag,
            categoryName: displayCategoryName,
          }),
        );
        showModal(AuthModalScreen.EnterEmail);
        return;
      }
      setSelectedTagForFlag(mainTag);
      setSelectedCategoryName(displayCategoryName);
      setIsFlagVenueOpen(true);
    }
  };

  const handleOpenFullImageView = () => {
    if (currentSpace && currentSpace.photos.length > 0) {
      setFullImageView({
        show: true,
        current: currentImageIndex,
      });
      const eventName = getEventName("accessibilityFullImageOpen");
      AnalyticsService.trackGAEventHandler(eventName);
      AnalyticsService.trackMx(eventName, {
        VenueId: venueDetails.business.id,
        VenueBrandId: venueDetails.brandId,
        VenueBrandName: venueDetails.brandName,
        VenueAddress: venueDetails.address,
        VenueName: venueDetails.name,
        VenueType: venueDetails.businessType,
        MainTag: currentSpace.name,
      });
    }
  };

  const handleCloseFullImageView = () => {
    setFullImageView({
      show: false,
      current: 0,
    });
    const eventName = getEventName("accessibilityFullImageClose");
    AnalyticsService.trackGAEventHandler(eventName);
    AnalyticsService.trackMx(eventName, {
      VenueId: venueDetails.business.id,
      VenueBrandId: venueDetails.brandId,
      VenueBrandName: venueDetails.brandName,
      VenueAddress: venueDetails.address,
      VenueName: venueDetails.name,
      VenueType: venueDetails.businessType,
      MainTag: currentSpace?.name || "",
    });
  };

  const handleOpenFlagTracking = () => {
    const eventName = getEventName("flagAccessibilityReportOpen");
    AnalyticsService.trackGAEventHandler(eventName);
    AnalyticsService.trackMx(eventName, {});
  };

  return (
    <div className="flex flex-col lg:flex-row h-full bg-bg-brand-secondary-default rounded-xl overflow-hidden">
      {/* Screen reader announcement for selection changes */}
      <div role="status" aria-live="polite" className="sr-only">
        {selectedSpaceId && currentSpace ? `${i18n.get("nowShowing")} ${currentSpace.name}` : ''}
      </div>

      {/* Backdrop for mobile drawer - only visible on mobile when sidebar is open */}
      {showSidebar && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setShowSidebar(false)}
          aria-hidden="true"
        />
      )}

      {/* Left Sidebar - Space Navigation */}
      {/* Desktop: inline with transition, Mobile: full-width overlay drawer */}
      <div
        className={`
          lg:relative lg:overflow-hidden lg:h-full
          ${showSidebar ? "lg:w-[314px] lg:flex-shrink-0" : "lg:w-0"}
          lg:transition-all lg:duration-300
        `}
      >
        {/* SpaceSidebar -> Mobile: Full-width fixed overlay drawer starting from header, Desktop: normal inline content */}
        <div
          className={`
            fixed top-[85px] left-0 right-0 bottom-0 w-full z-[100]
            transform transition-transform duration-300 ease-in-out
            ${showSidebar ? "translate-x-0" : "-translate-x-full"}
            lg:top-0 lg:relative lg:inset-auto lg:w-full lg:h-full lg:transform-none lg:transition-none lg:z-auto
          `}
          role="dialog"
          aria-modal="true"
          aria-label="Accessibility spaces sidebar"
        >
          <SpaceSidebar
            spaces={spaces}
            categories={categories}
            selectedSpaceId={selectedSpaceId}
            onSelectSpace={handleSelectSpace}
            expandedCategories={expandedCategories}
            onToggleCategory={handleToggleCategory}
            onToggleSidebar={() => setShowSidebar(!showSidebar)}
            isSidebarOpen={showSidebar}
          />
        </div>
      </div>

      {/* Main Content Container */}
      <div className={`flex-1 flex flex-col min-w-0 ${isZoomedIn ? 'overflow-y-auto' : 'overflow-hidden'}`}>
        {/* Header - Navigation */}
        <SpaceNavigationHeader
          venueName={venueDetails.name}
          currentSpace={currentSpace}
          onPrevious={handlePreviousSpace}
          onNext={handleNextSpace}
          onToggleSidebar={() => setShowSidebar(!showSidebar)}
          onBack={onClose}
          isSidebarOpen={showSidebar}
          hasPrevious={currentNonEmptyIndex > 0}
          hasNext={currentNonEmptyIndex < nonEmptySpaces.length - 1}
        />

        {/* Content: Image + Tags Panel (side by side on desktop, stacked on mobile) */}
        <div 
          role="region"
          aria-label={`Details for ${currentSpace?.name || 'selected space'}`}
          className={`flex-1 flex flex-col lg:flex-row lg:items-stretch ${isZoomedIn ? 'overflow-visible' : 'overflow-y-auto'}`}
        >
          {/* Image Section */}
          <div className="flex-1 relative bg-container-brand-minimal overflow-hidden min-h-[300px] lg:min-h-0 flex-shrink-0">
            <AccessibilityImageCarousel
              key={selectedSpaceId || ""}
              photos={currentSpace?.photos || []}
              currentIndex={currentImageIndex}
              onIndexChange={setCurrentImageIndex}
              onImageClick={handleOpenFullImageView}
              isZoomedIn={isZoomedIn}
              pageName={pageName}
            />
          </div>

          {/* Accessibility Tags Panel - Below on mobile, right side on desktop */}
          <div className="w-full lg:w-80 border-t lg:border-t-0 lg:border-l border-gray-200 bg-bg-brand-secondary-default overflow-y-auto flex flex-col flex-shrink-0">
            <AccessibilityTagsPanel
              spaceName={currentSpace?.name || ""}
              spaceId={selectedSpaceId || ""}
              tags={currentSpace?.tags || []}
              displaySections={
                currentSpace?.categoryId
                  ? displaySectionsMap[currentSpace.categoryId]
                  : undefined
              }
              alertsCount={0}
              onFlag={handleFlag}
              onShare={onShare}
            />
          </div>
        </div>
      </div>

      {/* Full Image Viewer Modal */}
      {fullImageView.show &&
        currentSpace &&
        createPortal(
          <div className="fixed inset-0 z-[999] bg-black bg-opacity-90">
            <FullImageViewer
              images={currentSpace.photos.map((photo) => ({
                src: bucketUrlPrefix + photo.path,
                alt: photo.alt_text || "",
                tag_name: photo.tag_name,
                category_name: photo.tag_applied,
                created_at: photo.created_at || "",
                width: photo.width,
                height: photo.height,
                qtags: currentSpace.tags.map((tag) => ({
                  tagId: tag.tagId,
                  tagName: tag.tagName,
                  categoryDisplaySectionId:
                    tag.categoryDisplaySectionId?.toString(),
                })),
                tags: currentSpace.tags.map((tag) => ({
                  tagName: tag.tagName,
                  categoryDisplaySectionId:
                    tag.categoryDisplaySectionId?.toString(),
                })),
                displaySections: currentSpace.categoryId
                  ? displaySectionsMap[currentSpace.categoryId]
                  : undefined,
                venueId: venueDetails.business.id,
                venueName: venueDetails.name,
                venueAddress: venueDetails.address,
                venueBrandId: venueDetails.brandId,
                venueBrandName: venueDetails.brandName,
                venueTypes: venueDetails.businessType,
                user_roles: {
                  isGeneralUser: false,
                },
              }))}
              currentIndex={fullImageView.current}
              onClose={handleCloseFullImageView}
              onFlag={() => {
                if (currentSpace.tags.length > 0) {
                  handleOpenFlagTracking();
                  handleFlag();
                }
              }}
              showFlag={true}
              showRightTab={true}
            />
          </div>,
          document.body,
        )}

      {/* Flag Venue Accessibility Modal */}
      <FlagVenueAccessibility
        venueId={venueDetails.business.id.toString()}
        venueName={venueDetails.name}
        mainTag={
          venueDetails.templateType === "Complex" ? "" : selectedTagForFlag
        }
        catName={selectedCategoryName}
        isOpen={isFlagVenueOpen}
        onClose={() => handleFlagVenueAccessibilityClose()}
        venueDetails={venueDetails}
      />
    </div>
  );
}

export default AccessibilityDetailView;
